import { Routes } from '@angular/router';
import { Component } from '@angular/core';
import { authGuard, posGuard, kdsGuard } from './core/guards/auth.guard';
import { posAuthGuard } from './features/pos/pos-auth.guard';
import { posStandaloneGuard } from './features/pos/pos-standalone.guard';
import { posPaymentGuard, posPermissionGuard } from './features/pos/pos-permission.guard';
import { kdsLaunchResolver } from './features/kds/kds-launch.service';
import { cfdLaunchResolver } from './features/customer-display/customer-display-launch.service';
// PTM-4 — Payment Terminal anti-leak guard. Listed FIRST on every core `pos*`
// route below (guard priority is array order): on a device marked as a
// Payment Terminal every road into the full POS ends on /pos/s/<code>/pt.
import { paytermDeviceRedirectGuard } from './features/payterm/payterm.guards';

/**
 * Fallback for any unknown path. Shows a minimal, theme-neutral card and
 * lets the operator bounce back to the PIN screen. We intentionally avoid
 * a noisy 404 page — POS terminals must always have a clean recovery path.
 */
@Component({
  selector: 'app-not-found',
  standalone: true,
  template: `
    <main style="min-height: 100dvh; display: grid; place-items: center; padding: 24px; text-align: center; background: #f8f9fb;">
      <div style="max-width: 320px;">
        <div style="font-size: 56px; line-height: 1; color: #C41E3A;" aria-hidden="true">⌫</div>
        <h1 style="font-size: 20px; font-weight: 700; margin: 16px 0 8px; color: #0f172a;">Page not found</h1>
        <p style="font-size: 14px; color: #6b7280; margin: 0 0 20px;">This terminal route is no longer valid.</p>
        <a href="/pos" style="display: inline-block; padding: 10px 20px; background: #C41E3A; color: #fff; border-radius: 8px; text-decoration: none; font-weight: 600;">Back to PIN</a>
      </div>
    </main>
  `,
})
export class NotFoundComponent {}

/**
 * The new POS workspace IS the POS app, but every URL still carries the
 * `/pos/*` prefix that the legacy admin-bundled routes used. Preserving the
 * prefix means: (a) bookmarks the operator already has keep working;
 * (b) the Flutter edge client's hard-coded URLs (`/pos/s/<code>`,
 * `/pos/terminal`, …) keep working with no shell update; (c) backend code
 * that mints redirect URLs to `/pos/...` keeps working. The literal path
 * cost is one segment — negligible.
 *
 * The empty path (`/`) just redirects into `/pos`.
 */
export const routes: Routes = [
  // Root → PIN. The redirect preserves the case where the operator types
  // the bare host into the address bar.
  { path: '', pathMatch: 'full', redirectTo: 'pos' },

  // Terminal-setup login (email + password). Entry point for the Flutter
  // edge-client shell on first launch — it deep-links here with
  // `?source=edgeclient&deviceId=<hw-guid>`. After a successful sign-in the
  // operator is routed through /select-store → /edge-clients/register/:code
  // → /pos/s/:code (PIN screen) to bring up the terminal. Subsequent shell
  // launches skip this step entirely and deep-link straight to /pos/s/:code.
  {
    path: 'login',
    loadComponent: () => import('./features/login/login.component').then(m => m.LoginComponent),
  },

  // POS PIN-login entry (admin-launched).
  {
    path: 'pos',
    pathMatch: 'full',
    canActivate: [paytermDeviceRedirectGuard],
    loadComponent: () => import('./features/pos/pin-login/pin-login.component').then(m => m.PinLoginComponent),
  },

  // PTM-3 — Payment Terminal (pay-only handheld) PIN entry. Under
  // /pos/s/<code>/ on purpose — today's Flutter shell treats it as a POS page
  // (store capture, app sign-in, payment-config sync, print polling, and its
  // Exit ✕ / Settings ⚙) with no edge release. Registered BEFORE the PIN deep
  // link below so the longer path is matched first.
  // docs/PAYTERM-PAYMENT-TERMINAL-MODE-PLAN.md §6.1.
  {
    path: 'pos/s/:posStoreCode/pt',
    loadChildren: () => import('./features/payterm/payterm.routes').then(m => m.paytermEntryRoutes),
  },
  // PTM-6 — the Payment Terminal working screens (tables, settle). Outside
  // /pos/s/<code>/ so the edge shows its controls on the PIN screen only — the
  // same split as /pos/s/<code> → /pos/terminal. Guarded inside by
  // paytermAuthGuard; no device redirect here, this IS the terminal surface.
  {
    path: 'pos/pt',
    loadChildren: () => import('./features/payterm/payterm.routes').then(m => m.paytermRoutes),
  },

  // Standalone PIN deep link from the Flutter edge client.
  // The shell launches the WebView at /pos/s/<storeCode> after device
  // registration.
  {
    path: 'pos/s/:posStoreCode',
    // EXPIRY-LOGIN — persists the store code before the PIN component or any
    // bootstrap call runs (the guard existed, documented, but was never wired).
    // PTM-4 — the payterm redirect stays FIRST so a Payment Terminal device
    // never lands on the full-POS PIN screen.
    canActivate: [paytermDeviceRedirectGuard, posStandaloneGuard],
    loadComponent: () => import('./features/pos/pin-login/pin-login.component').then(m => m.PinLoginComponent),
  },

  // Edge Client device registration — reached from the store-selection
  // flow when the Flutter shell first launched the WebView. Calls
  // /api/edge-clients/{lookup,register,…} and routes onward to /pos/s/:code.
  {
    path: 'edge-clients/register/:posStoreCode',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/edge-client/edge-client-registration.component')
        .then(m => m.EdgeClientRegistrationComponent),
  },

  // Store-selection — used when an admin-launched session has multiple
  // tenants/stores in scope. The edge client's first launch also lands here.
  {
    path: 'select-store',
    canActivate: [authGuard],
    loadComponent: () => import('./features/store-selection/store-selection.component').then(m => m.StoreSelectionComponent),
  },

  // ── POS workspace ────────────────────────────────────────────────
  {
    path: 'pos/terminal',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/pos-shell/pos-shell.component').then(m => m.PosShellComponent),
  },

  // TA-2 — the Time Clock. Its own full-screen surface rather than a panel inside the POS
  // shell: the people using it are mostly NOT POS operators (cleaners, porters, kitchen
  // staff), and putting a clock-in behind the order screen would mean every punch walks
  // through a surface those people have no business seeing.
  //
  // Guarded like the terminal, because the punch endpoints require a POS token in Rev 2 —
  // the till is open and staff walk up to it. Unattended operation needs the
  // edge-authenticated punch endpoint, which lands with TA-4.
  //
  // Registered HERE and not in features/pos/pos.routes.ts, which exports a route table that
  // nothing imports — adding a route there produces a screen that is silently unreachable.
  {
    path: 'pos/time-clock',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/time-clock/time-clock.component').then(m => m.TimeClockComponent),
  },
  {
    path: 'pos/order/:checkId',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/order-screen/order-screen.component').then(m => m.OrderScreenComponent),
  },
  {
    // SO-2 — the order screen in BOOKING mode: the same screen editing a future
    // booking's items (docs/SCHEDULED-ORDERS-PLAN.md D2). A sibling route, not an
    // input — the screen is routed and the app has no component input binding —
    // read as route data. The booking id rides the same :checkId slot the menu
    // reads already resolve.
    path: 'pos/booking/:checkId',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPermissionGuard('pos.scheduled.manage')],
    loadComponent: () => import('./features/pos/order-screen/order-screen.component').then(m => m.OrderScreenComponent),
    data: { mode: 'booking' },
  },
  {
    // SO-2 — the scheduled-orders board, standalone. The POS shell mounts the same
    // component inline from its MANAGEMENT card; this route is for deep links.
    path: 'pos/scheduled-orders',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPermissionGuard('pos.scheduled.manage')],
    loadComponent: () => import('./features/pos/scheduled-orders/scheduled-orders-board.component').then(m => m.ScheduledOrdersBoardComponent),
  },
  {
    // P2 / Bug B — single route-level choke-point for settlement. posPaymentGuard
    // is PIN-identity authoritative (posState.canSettlePayment from the PIN'd-in
    // user's roles), closing ALL paths to settle (table tap, order button,
    // direct URL) for waiter-only terminals. Deliberately NOT the generic
    // posPermissionGuard yet — that reads the leaky PermissionService cache;
    // it returns once Layer 2 makes PermissionService reflect the PIN user.
    path: 'pos/settle/:checkId',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPaymentGuard],
    loadComponent: () => import('./features/pos/settle-screen/settle-screen.component').then(m => m.SettleScreenComponent),
  },
  {
    // RECON-5 — actions the cloud never accepted. No permission guard:
    // this is a read of THIS terminal's queue plus a supervised retry /
    // discard, and it must be reachable by whoever is standing at the
    // till when the banner says something is stuck. The discard itself
    // records who and why.
    path: 'pos/sync-issues',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () =>
      import('./features/pos/sync-reconciliation/sync-reconciliation.component')
        .then(m => m.SyncReconciliationComponent),
  },
  {
    path: 'pos/x-report',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/x-report/x-report.component').then(m => m.XReportComponent),
  },
  {
    // RPT-9 — POS custom reports (Revenue / Item Sales / Cashier). No permission
    // guard: a cashier must reach the screen so the manager-PIN override can run
    // (the run/print endpoints are gated server-side by pos.reports.view + override).
    path: 'pos/reports',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/reports/pos-reports.component').then(m => m.PosReportsComponent),
  },
  {
    // Phase 4 §C — manager-gated biometric enrolment wizard. Reached from the
    // edge client's Settings → FINGERPRINT → "Open Enrolment" (kiosk has no
    // address bar). The component itself bounces non-managers to the terminal.
    path: 'pos/settings/biometric-enrol',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/biometric/biometric-enrol.component').then(m => m.BiometricEnrolComponent),
  },
  {
    // TA-4 — attendance fingerprint enrolment. A sibling of the POS-login
    // wizard above, not an extension of it: separate endpoints, separate
    // permission, separate credential table. Enrolling somebody for the time
    // clock must not be one wrong dropdown away from giving them a POS login.
    path: 'pos/settings/attendance-enrol',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () =>
      import('./features/time-clock/attendance-enrol.component').then(
        (m) => m.AttendanceEnrolComponent,
      ),
  },
  {
    // Gated on pos.session-orders.view (Layer 2 keys PermissionService to the
    // PIN user, so the guard is authoritative). Unpermitted operators redirect
    // to the terminal with ?denied=pos.session-orders.view.
    path: 'pos/session-orders',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPermissionGuard('pos.session-orders.view')],
    loadComponent: () => import('./features/pos/session-orders/session-orders.component').then(m => m.SessionOrdersComponent),
  },
  {
    // RET-2 — linked return drafts. Gated on pos.return.view (Layer 2 keys
    // PermissionService to the PIN user, so the guard is authoritative).
    // Creating a draft additionally needs pos.return.create or a manager PIN
    // override, enforced server-side. Online-only; no money movement.
    path: 'pos/returns',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPermissionGuard('pos.return.view')],
    loadComponent: () => import('./features/pos/returns/returns.component').then(m => m.ReturnsComponent),
  },
  {
    path: 'pos/queue',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/qsr-queue/qsr-queue.component').then(m => m.QsrQueueComponent),
  },
  {
    path: 'pos/external-orders',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/external-orders/external-orders.component').then(m => m.ExternalOrdersComponent),
  },
  {
    // RES-B5 — Host Workspace (Today | Reservations | Waitlist | Floor).
    // Same component the POS shell mounts inline; rendered standalone here
    // for direct deep-links and bookmarks.
    path: 'pos/host',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/host/host-workspace.component').then(m => m.HostWorkspaceComponent),
  },
  {
    // DLV-2 — Delivery Command Center (operational kanban).
    path: 'pos/delivery',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard],
    loadComponent: () => import('./features/pos/delivery/delivery-command-center.component').then(m => m.DeliveryCommandCenterComponent),
  },
  {
    // DLV-5 — Driver-cash settlement screen.
    // P6 — gated on delivery.driver.settle (Layer 2 keys PermissionService to
    // the PIN user, so the guard is correct). Waiters / unpermitted operators
    // redirect to the terminal with ?denied=delivery.driver.settle.
    path: 'pos/delivery/driver-settlement/:driverId',
    canActivate: [paytermDeviceRedirectGuard, posGuard, posAuthGuard, posPermissionGuard('delivery.driver.settle')],
    loadComponent: () => import('./features/pos/delivery/driver-settlement.component').then(m => m.DriverSettlementComponent),
  },

  // ── KDS workspace ────────────────────────────────────────────────
  // Two doorways, two trust models:
  //
  //   /kds              — admin-launched (legacy). Auth via tenant JWT.
  //                       Guarded by kdsGuard (reads StoreContextService).
  //                       Used when a manager opens KDS from inside the
  //                       admin shell on a regular workstation.
  //
  //   /kds/s/<code>     — physical-tablet entry (POS-workspace, Path A).
  //                       Auth via storeCode → kdsToken minted by
  //                       /api/public/store/{code}/kds-launch. NO tenant
  //                       login required. Same trust model as kiosk +
  //                       pickup-display. Resolver fires the launch
  //                       before the screen mounts; on failure routes
  //                       to /kds-unavailable.
  {
    path: 'kds',
    canActivate: [kdsGuard],
    loadChildren: () => import('./features/kds/kds.routes').then(m => m.kdsRoutes),
  },
  {
    path: 'kds/s/:storeCode',
    resolve: { kdsLaunch: kdsLaunchResolver },
    loadChildren: () => import('./features/kds/kds.routes').then(m => m.kdsRoutes),
  },
  {
    // Uniform fallback for any KDS launch failure (unknown storeCode /
    // inactive store / network unreachable). Plain card, no enumeration
    // of why it failed.
    path: 'kds-unavailable',
    loadComponent: () => import('./features/kds/kds-unavailable.component').then(m => m.KdsUnavailableComponent),
  },

  // ── Kiosk (self-order touchscreen) ───────────────────────────────
  // storeCode is the entry credential — the kiosk is a physical screen
  // in the store, so the URL on the QR sticker is the trust surface.
  // Backend resolves storeCode → kiosk publicToken → returns the full
  // config; the shell then uses the embedded publicToken for menu /
  // cart / submit calls without further changes.
  {
    path: 'kiosk/s/:storeCode',
    loadComponent: () => import('./features/kiosk/kiosk-shell/kiosk-shell.component').then(m => m.KioskShellComponent),
  },

  // ── Pickup Display (counter TV board) ────────────────────────────
  // Same trust model as kiosk. Polls /api/public/store/:storeCode/pickup-display
  // every few seconds for the in-progress / ready / picked-up board.
  {
    path: 'pickup-display/s/:storeCode',
    loadComponent: () => import('./features/pickup-display/pickup-display.component').then(m => m.CustomerPickupDisplayComponent),
  },

  // ── Customer Facing Display (rear second screen) ─────────────────
  // Same trust model as kiosk / KDS / pickup-display: storeCode is the
  // credential. cfdLaunchResolver mints a least-privilege CustomerDisplay
  // token before the shell mounts; on failure it redirects to
  // /customer-display-unavailable. The shell is read-only — it mirrors the
  // active check (via a local BroadcastChannel pointer + AppSync) and shows
  // attract media when idle.
  {
    path: 'customer-display/s/:storeCode',
    resolve: { cfdLaunch: cfdLaunchResolver },
    loadChildren: () => import('./features/customer-display/customer-display.routes').then(m => m.customerDisplayRoutes),
  },
  {
    path: 'customer-display-unavailable',
    loadComponent: () => import('./features/customer-display/cfd-unavailable.component').then(m => m.CfdUnavailableComponent),
  },

  // Any unknown path falls back to the NotFound card whose CTA goes back
  // to the PIN screen (never to admin login).
  { path: '**', component: NotFoundComponent },
];
